import { useAuth } from "context/AuthContext.jsx";
import ChatMessage from "component/chat/ChatMessage.jsx";
import ChatInput from "component/chat/ChatInput.jsx";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {getUUID} from "utils/uuid.js";
import { apiUrl } from "config/api";

const backgroundVideoUrl = "/background_new.mp4";

// Helper: read stored access token from localStorage
function getStoredAccessToken() {
  try {
    return (
      localStorage.getItem('access_token') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      ''
    );
  } catch {
    return '';
  }
}

export default function Chat() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [userId, setUserId] = useState(() => {
    const v = sessionStorage.getItem('user_id');
    return v ? Number(v) : undefined;
  });
  const [progress, setProgress] = useState(() => {
    const p = sessionStorage.getItem('onboarding_progress');
    return p ? Number(p) : 0;
  });

  const resolveUserId = async () => {
    if (user?.id) {
      const id = Number(user.id);
      setUserId(id);
      sessionStorage.setItem('user_id', String(id));
      return id;
    }
    const token = getStoredAccessToken();
    if (!token) return undefined;
    const meRes = await fetch(apiUrl('/api/auth/me'), {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (meRes.status === 401) {
      navigate('/login', { replace: true });
      return undefined;
    }
    if (!meRes.ok) return undefined;
    const me = await meRes.json();
    if (!me?.id) return undefined;
    const id = Number(me.id);
    setUserId(id);
    sessionStorage.setItem('user_id', String(id));
    return id;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // If we already have a userId, nothing to do
      if (userId !== undefined) return;

      // If AuthContext has a user, adopt it
      if (user?.id) {
        if (!cancelled) {
          setUserId(Number(user.id));
          sessionStorage.setItem('user_id', String(user.id));
        }
        return;
      }

      // Try to hydrate from token and verify with the server
      const token = getStoredAccessToken();
      if (!token) return; // don't redirect yet; allow Login page to handle

      try {
        const meRes = await fetch(apiUrl('/api/auth/me'), {
          credentials: 'include',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (meRes.ok) {
          const me = await meRes.json();
          if (me?.id) {
            setUserId(Number(me.id));
            sessionStorage.setItem('user_id', String(me.id));
          }
        } else if (meRes.status === 401) {
          navigate('/login', { replace: true });
        }
      } catch {
        // On network error, stay put (no redirect loop)
      }
    })();
    return () => { cancelled = true; };
  }, [userId, user?.id, navigate]);
  useEffect(() => {
    if (userId === undefined && user?.id) {
      setUserId(Number(user.id));
      sessionStorage.setItem('user_id', String(user.id));
    }
  }, [userId, user?.id]);

  useEffect(() => {
    const maybe = location?.state?.userId;
    if (maybe && Number(maybe) !== userId) {
      setUserId(Number(maybe));
      sessionStorage.setItem('user_id', String(maybe));
    }
  }, [location?.state?.userId]);

  const scrollContainerRef = useRef(null);
  const endOfMessagesRef = useRef(null);
  const videoRef = useRef(null);

  const typingKeyframes = `
  @keyframes dotBlink { 0% { opacity: 0.3; } 50% { opacity: 1; } 100% { opacity: 0.3; } }
  @keyframes dotBounce { 0%, 80%, 100% { transform: translateY(0); } 40% { transform: translateY(-4px); } }
  `;

  useEffect(() => {
    // Use a frame to ensure DOM has updated heights
    const id = requestAnimationFrame(() => {
      if (endOfMessagesRef.current) {
        endOfMessagesRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
      } else if (scrollContainerRef.current) {
        const el = scrollContainerRef.current;
        el.scrollTop = el.scrollHeight;
      }
    });
    return () => cancelAnimationFrame(id);
  }, [messages]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const tryPlay = () => {
      v.muted = true; // required by autoplay policies
      const p = v.play?.();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          // ignore play rejections
        });
      }
    };

    // kick off once mounted
    tryPlay();

    const onVisibility = () => {
      if (!document.hidden) tryPlay();
    };
    const onFocus = () => tryPlay();
    const onPause = () => tryPlay();
    const onEnded = () => tryPlay();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    v.addEventListener('pause', onPause);
    v.addEventListener('ended', onEnded);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      v.removeEventListener('pause', onPause);
      v.removeEventListener('ended', onEnded);
    };
  }, []);

  const sendMessage = async (text) => {
    const mine = { id: getUUID(), role: "user", text };
    // Optimistically render the user message first
    setMessages((m) => [...m, mine]);

    try {
      setIsTyping(true);
      let uid = userId;
      if (!uid) {
        uid = await resolveUserId();
      }
      if (!uid) {
        throw new Error('Not authenticated');
      }
      const token = getStoredAccessToken();
      let res = await fetch(apiUrl(`/api/onboarding/chat/${uid}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ text }),
      });

      if (res.status === 404) {
        const fallbackUid = await resolveUserId();
        if (fallbackUid && fallbackUid !== uid) {
          res = await fetch(apiUrl(`/api/onboarding/chat/${fallbackUid}`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: 'include',
            body: JSON.stringify({ text }),
          });
        }
      }

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `HTTP ${res.status}`);
      }

      const data = await res.json();
      // New response format: progress percentage from backend (monotonic)
      if (typeof data?.progress_pct !== 'undefined') {
        const pct = Number(data.progress_pct);
        if (!Number.isNaN(pct)) {
          setProgress(prev => {
            const next = Math.max(0, Math.min(100, Math.max(prev, Math.round(pct))));
            sessionStorage.setItem('onboarding_progress', String(next));
            return next;
          });
        }
      }

      // Detect completion flag either as a field or embedded in text
      let complete = false;
      if (data && data.complete === true) {
        complete = true;
      }

      let cleanedText = typeof data.text === 'string' ? data.text : '';
      if (!complete && typeof cleanedText === 'string' && /"complete"\s*:\s*true/i.test(cleanedText)) {
        complete = true;
        cleanedText = cleanedText.replace(/\{\s*"complete"\s*:\s*true\s*\}/gi, '').trim();
      }

      const ai = {
        id: getUUID(),
        role: data.sender === 'ai' ? 'assistant' : (data.sender || 'assistant'),
        text: cleanedText ?? '',
      };
      setMessages((m) => [...m, ai]);

      if (complete) {
        setIsCompleted(true);
        setProgress((prev) => Math.max(prev, 100));
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          id: getUUID(),
          role: 'assistant',
          text: `${t("onboarding.chat.errorService")} ${e?.message ? '(' + e.message + ')' : ''}`,
        },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div style={{ position: 'relative', minHeight: '100vh', color: '#FFFFFF' }}>
      <video
        ref={videoRef}
        src={backgroundVideoUrl}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        controls={false}
        disablePictureInPicture
        controlsList="nodownload noplaybackrate noremoteplayback"
        style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0, pointerEvents: 'none' }}
      />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <style>{typingKeyframes}</style>
        {/* Top bar with user + logout (kept minimal) */}
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 24px' }}>
          <div />
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: 0.8, fontSize: 14 }}>
            <span>{user?.email}</span>
            <button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              style={{
                display: 'flex',
                width: 140,
                height: 36,
                padding: '10px 16px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 10,
                flexShrink: 0,
                borderRadius: 5,
                background: 'transparent',
                backgroundBlendMode: 'plus-lighter, luminosity, darken',
                boxShadow: '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)',
                color: '#FFF',
                letterSpacing: 1,
                fontWeight: 500,
                border: 'none'
              }}
            >
              {t("nav.logout")}
            </button>
          </div>
        </header>

        {/* Segmented control */}
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
          <div
            style={{
              display: 'inline-flex',
              padding: 4,
              borderRadius: 9999,
                background: 'transparent',
                backgroundBlendMode: 'plus-lighter, luminosity, darken',
                boxShadow: '-2px 0 2px rgba(255, 255, 255, 0.3), 2px 0 2px rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)'
            }}
          >
            <button
              style={{
                padding: '8px 24px',
                borderRadius: 9999,
                fontSize: 14,
                fontWeight: 600,
                background: 'linear-gradient(0deg, rgba(255,255,255,0.14), rgba(255,255,255,0.06))',
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.18)',
                color: '#fff',
                cursor: 'default',
                border: 'none'
              }}
            >
              {t("onboarding.chat.tabChat")}
            </button>
            <button
              style={{
                padding: '8px 24px',
                borderRadius: 9999,
                fontSize: 14,
                fontWeight: 600,
                opacity: 0.7,
                color: '#fff',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              {t("onboarding.chat.tabMeeting")}
            </button>
          </div>
        </div>

        {/* Glass card container background: 'linear-gradient(0deg, black 0%, black 100%), rgba(51, 51, 51, 0.15), rgba(153, 153, 153, 0.30)', */}
        <main style={{ position: 'relative', flex: 1 }}>
          <div
            style={{
              position: 'relative',
              margin: '0 auto',
              width: 'calc(100% - 120px)',
              maxWidth: 1094,
              minHeight: 600,
              borderRadius: 25,
              background: 'rgba(0, 0, 0, 0.55)',
              backgroundBlendMode: 'screen, luminosity, darken',
              boxShadow: '0px 0px 3px 0px #f2f2f2 inset, 0px 0px 3px 0px #ffffff80 inset, -1px -1px 0.5px -1px #ffffff inset, 1px 1px 0.5px -1px #ffffff inset, -1px -1px 0px -0.5px #262626 inset, 1px 1px 0px -0.5px #333333 inset',
              overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.14)'
            }}
          >
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(8, 8, 8, 0.03)', backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(3px)' }} />

            <div style={{ position: 'relative', padding: '36px 38px 90px 38px' }}>
              {/* Scroll container for welcome and messages */}
              <div
                style={{
                  overflowY: 'auto',
                  height: 'calc(100vh - 330px)',
                  paddingBottom: 180,
                }}
                ref={scrollContainerRef}
              >
                {/* Onboarding progress (single sticky container with label + percent + bar) */}
                <div
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 30,
                    marginBottom: 12,
                    padding: '8px 0',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    borderBottom: '1px solid rgba(255,255,255,0.10)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12, letterSpacing: 0.2 }}>{t("onboarding.chat.progress")}</div>
                    <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 12 }}>{Math.max(0, Math.min(100, Math.round(progress)))}%</div>
                  </div>
                  <div style={{ width: '100%', height: 8, borderRadius: 6, background: 'rgba(255,255,255,0.15)', overflow: 'hidden', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.12)' }}>
                    <div style={{ width: `${Math.max(0, Math.min(100, Math.round(progress)))}%`, height: '100%', background: 'linear-gradient(90deg, rgba(255,255,255,0.7), rgba(255,255,255,0.35))', transition: 'width 240ms ease', borderRadius: 6 }} />
                  </div>
                </div>
                {/* Static welcome block */}
                <div style={{ width: '100%', maxWidth: 760, marginTop: 10 }}>
                  
                  <div style={{ color: 'white', fontSize: 20, fontWeight: 600, lineHeight: '40px' }}>
                    {t("onboarding.chat.welcomeTitle")}
                  </div>
                  <div style={{ color: 'white', fontSize: 16, fontWeight: 400, lineHeight: '24px', marginTop: 8 }}>
                    {t("onboarding.chat.welcomeBody1")}
                    <br />
                    <br />
                    {t("onboarding.chat.welcomeBody2")}
                  </div>
                </div>

                {/* Dynamic messages */}
                <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', rowGap: 16 }}>
                  {messages.map((m) => (
                    <ChatMessage key={m.id} role={m.role} text={m.text} />
                  ))}
                </div>

                {isTyping && (
                  <div style={{ marginTop: 16, display: 'flex' }}>
                    <div
                      style={{
                        maxWidth: 320,
                        padding: '10px 14px',
                        borderRadius: 14,
                        background: 'rgba(255,255,255,0.08)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: 'inset 0 0 16px rgba(242,242,242,0.2)',
                        backdropFilter: 'blur(3px)',
                        WebkitBackdropFilter: 'blur(3px)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 9999, background: '#aaa', display: 'inline-block', animation: 'dotBlink 1.2s infinite, dotBounce 1.2s infinite', animationDelay: '0s, 0s' }} />
                        <span style={{ width: 8, height: 8, borderRadius: 9999, background: '#888', display: 'inline-block', animation: 'dotBlink 1.2s infinite, dotBounce 1.2s infinite', animationDelay: '0.2s, 0.2s' }} />
                        <span style={{ width: 8, height: 8, borderRadius: 9999, background: '#666', display: 'inline-block', animation: 'dotBlink 1.2s infinite, dotBounce 1.2s infinite', animationDelay: '0.4s, 0.4s' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={endOfMessagesRef} />
              </div>

              {/* Input row / completion CTA */}
              <div style={{
                position: 'absolute',
                left: 46,
                right: 98,
                bottom: 18,
                minHeight: 42,
                height: 'auto',
                borderRadius: 24,
                overflow: 'visible',
              }}>
                <div style={{ position: 'absolute', inset: 0 }} />
                <div style={{
                  position: 'relative',
                  minHeight: 42,
                  height: 'auto',
                  padding: '0 16px',
                  display: 'flex',
                  alignItems: 'stretch',
                }}>
                  {isCompleted ? (
                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                      <button
                        type="button"
                        onClick={() => navigate('/start')}
                        style={{
                          minWidth: 320,
                          height: 42,
                          borderRadius: 24,
                          border: '1px solid rgba(255,255,255,0.22)',
                          color: '#fff',
                          fontWeight: 600,
                          background: 'linear-gradient(0deg, rgba(255,255,255,0.16), rgba(255,255,255,0.06))',
                          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.14)',
                          cursor: 'pointer',
                        }}
                      >
                        {t("onboarding.chat.continueToWaiting")}
                      </button>
                    </div>
                  ) : (
                    <div style={{ flex: 1 }}>
                      <ChatInput onSend={sendMessage} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
